import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { getAllClientsAPI } from "api/apiHelper";
import Table from "components/Table";
import Card from "components/Card";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";

interface ClientData {
    id: number;
    name: string;
    email: string | null;
    city: string | null;
    country: string | null;
    status: 'ACTIVE' | 'INACTIVE' | 'ON_HOLD'; 
    logo: string | null;
}

const ClientList = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState<ClientData[]>([]);
    const columnHelper = createColumnHelper<ClientData>();

    useEffect(() => {
        getAllClientsAPI()
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
                setClients(data);
                console.log(clients);
            })
            .catch(err => console.error("Failed to fetch clients", err));
    }, []);

    // Helper for badge colors
    const getStatusColor = (status: string) => {
        if(status === 'ACTIVE') return "bg-green-100 text-green-700";
        if(status === 'ON_HOLD') return "bg-orange-100 text-orange-700";
        return "bg-red-100 text-red-700";
    };

    const columns: ColumnDef<ClientData, any>[] = [
        columnHelper.accessor("name", {
            header: () => <TableHeader>Client</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <span className="font-bold text-navy-700 dark:text-white">
                        {info.getValue()}
                    </span>
                </TableCell>
            ),
        }),
        columnHelper.accessor("city", {
            header: () => <TableHeader>Location</TableHeader>,
            cell: (info) => (
                <TableCell>
                    {info.getValue() || "N/A"}, {info.row.original.country || ""}
                </TableCell>
            ),
        }),
        columnHelper.accessor("email", {
            header: () => <TableHeader>Email</TableHeader>,
            cell: (info) => <TableCell className="font-medium">{info.getValue()}</TableCell>,
        }),
        columnHelper.accessor("status", {
            header: () => <TableHeader>Status</TableHeader>,
            cell: (info) => {
                const status = info.getValue();
                return (
                    <TableCell>
                        <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${getStatusColor(status)}`}>
                            {status.replace("_", " ")}
                        </span>
                    </TableCell>
                );
            },
        }),
        columnHelper.display({
            id: "actions",
            header: () => <TableHeader>Actions</TableHeader>,
            cell: (info) => (
                <TableCell>
                    <button
                        onClick={() => navigate(`/client-management/${info.row.original.id}`)}
                        className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm rounded-lg font-medium transition-colors"
                    >
                        Manage
                    </button>
                </TableCell>
            ),
        }),
    ];

    return (
        <div className="mt-5">
            <Card extra="w-full p-4 bg-white dark:bg-navy-800 rounded-xl shadow-sm">
                <div className="flex justify-between items-center mb-4 px-2">
                    <h2 className="text-xl font-bold text-navy-700 dark:text-white">Registered Companies</h2>
                </div>
                <Table tableData={clients} columns={columns} tableHeading={''} />
            </Card>
        </div>
    );
};

export default ClientList;